import { inject, Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { OnPremEnvironment } from '../../../../environments/environment.model';
import { EnvironmentConfigService } from '../../core/environment-config/environment-config.service';
import { EnvironmentConfiguration } from '../../core/environment-config/environment-configuration.interface';
import { REST_BASE_URL, REST_BASE_URL_WR } from '../../core/global';

import { ContextService } from './context.service';

@Injectable()
export class ContextServiceImpl extends ContextService {
  private readonly environmentConfigService = inject(EnvironmentConfigService);

  public getContext(): EnvironmentConfiguration | OnPremEnvironment | null {
    if (environment.deploymentTarget === 'onprem') {
      // On-prem: environment values are baked in at build time.
      return environment;
    }
    // Cloud: values come from the runtime config.
    return this.environmentConfigService.snapshot();
  }

  public isLocalEnv(): boolean {
    if (environment.deploymentTarget === 'onprem') {
      return environment.ENV === 'local';
    }
    return this.environmentConfigService.resolvedEnv === 'local';
  }

  public getBaseUrl(url: string): HTMLAnchorElement {
    /* eslint-disable no-restricted-globals */
    const url1 = document.createElement('a');
    url1.href = document.querySelectorAll('base')[0].href;
    /* eslint-enable no-restricted-globals */
    url1.hash = '';
    url1.pathname = '';
    url1.search = '';
    //Because of IE
    url1.href = String(url1.href).replace(/\?#/, '');

    // Only backend requests get rewritten onto a backend host/port. Everything else
    // (assets, in-app navigation targets such as the logout URL) stays on the FE origin.
    const isWrRequest = url.includes(REST_BASE_URL_WR);
    const isRcRequest = url.includes(REST_BASE_URL);
    if (!isWrRequest && !isRcRequest) {
      return url1;
    }

    if (environment.deploymentTarget === 'onprem') {
      // On-prem: same-origin. In local dev override the port per backend
      // (tur-wr endpoints use the WR port, everything else uses the RC port);
      // on the server the FE and backend share host + port, so no override.
      if (environment.ENV === 'local') {
        url1.port = isWrRequest ? (environment.API_WR_PORT ?? '') : environment.API_PORT;
      }
      return url1;
    }

    // Cloud: the backend host comes from the runtime config
    // (RC -> API_URL, tur-wr -> API_WR_URL). The context path stays in req.url.
    const config = this.environmentConfigService.snapshot();
    const apiHost = isWrRequest ? config?.API_WR_URL : config?.API_URL;
    if (apiHost) {
      url1.href = apiHost;
      url1.hash = '';
      url1.search = '';
    }
    return url1;
  }
}
